import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Users, Loader2, Copy, CheckCircle, Download } from 'lucide-react';

interface Resume {
  id: string;
  filename: string;
  created_at: string;
}

interface FitAnalysis {
  id: string;
  created_at: string;
}

interface Scripts {
  initial: string;
  follow_up_1: string;
  follow_up_2: string;
}

export default function NetworkScripts() {
  const { session } = useAuth();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [fitAnalyses, setFitAnalyses] = useState<FitAnalysis[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState('');
  const [selectedFitId, setSelectedFitId] = useState('');
  const [targetPerson, setTargetPerson] = useState('');
  const [targetCompany, setTargetCompany] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [scripts, setScripts] = useState<Scripts | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    loadResumes();
  }, []);

  useEffect(() => {
    if (selectedResumeId) {
      loadFitAnalyses();
    }
  }, [selectedResumeId]);

  async function loadResumes() {
    setLoading(true);
    const { data } = await supabase
      .from('resume_uploads')
      .select('id, filename, created_at')
      .order('created_at', { ascending: false });

    if (data) {
      setResumes(data);
    }
    setLoading(false);
  }

  async function loadFitAnalyses() {
    const { data } = await supabase
      .from('fit_analyses')
      .select('id, created_at')
      .eq('resume_id', selectedResumeId)
      .order('created_at', { ascending: false });

    if (data) {
      setFitAnalyses(data);
    }
  }

  async function generateScripts() {
    if (!selectedResumeId) return;

    setGenerating(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-network-scripts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            resumeId: selectedResumeId,
            fitAnalysisId: selectedFitId || null,
            targetPerson,
            targetCompany,
            targetRole,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate scripts');
      }

      const { scripts: generatedScripts } = await response.json();
      setScripts(generatedScripts);
    } catch (error: any) {
      console.error('Error:', error);
      alert(error.message);
    } finally {
      setGenerating(false);
    }
  }

  function copyScript(scriptKey: string, text: string) {
    navigator.clipboard.writeText(text);
    setCopied(scriptKey);
    setTimeout(() => setCopied(null), 2000);
  }

  function downloadScripts() {
    if (!scripts) return;

    let content = '========================================\n';
    content += 'NETWORK OUTREACH SCRIPTS\n';
    content += '========================================\n\n';
    content += `Target Person: ${targetPerson}\n`;
    content += `Target Company: ${targetCompany}\n`;
    content += `Target Role: ${targetRole}\n\n`;

    content += '========================================\n';
    content += 'INITIAL OUTREACH MESSAGE\n';
    content += '========================================\n\n';
    content += scripts.initial + '\n\n';

    content += '========================================\n';
    content += 'FOLLOW-UP MESSAGE #1 (After No Response)\n';
    content += '========================================\n\n';
    content += scripts.follow_up_1 + '\n\n';

    content += '========================================\n';
    content += 'FOLLOW-UP MESSAGE #2 (Value-Add)\n';
    content += '========================================\n\n';
    content += scripts.follow_up_2 + '\n';

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `network-scripts-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <Users className="w-8 h-8 text-green-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Network Scripts</h1>
          </div>
          <p className="text-gray-600">
            Generate personalized outreach messages to connect with decision makers
          </p>
        </div>

        {!scripts ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Resume *
                </label>
                <select
                  value={selectedResumeId}
                  onChange={e => setSelectedResumeId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="">Choose a resume...</option>
                  {resumes.map(resume => (
                    <option key={resume.id} value={resume.id}>
                      {resume.filename} - {new Date(resume.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>

              {selectedResumeId && fitAnalyses.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Specific Role (Optional)
                  </label>
                  <select
                    value={selectedFitId}
                    onChange={e => setSelectedFitId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">General outreach</option>
                    {fitAnalyses.map(fit => (
                      <option key={fit.id} value={fit.id}>
                        Role-specific - {new Date(fit.created_at).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Person
                  </label>
                  <input
                    type="text"
                    value={targetPerson}
                    onChange={e => setTargetPerson(e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Company
                  </label>
                  <input
                    type="text"
                    value={targetCompany}
                    onChange={e => setTargetCompany(e.target.value)}
                    placeholder="Acme Corp"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Role
                </label>
                <input
                  type="text"
                  value={targetRole}
                  onChange={e => setTargetRole(e.target.value)}
                  placeholder="VP of Engineering"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={generateScripts}
                disabled={!selectedResumeId || generating}
                className="w-full flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Scripts...
                  </>
                ) : (
                  <>
                    <Users className="w-5 h-5 mr-2" />
                    Generate Scripts
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6 flex gap-3">
              <button
                onClick={downloadScripts}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition"
              >
                <Download className="w-5 h-5 mr-2" />
                Download
              </button>
              <button
                onClick={() => setScripts(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
              >
                Create New
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Initial Outreach Message</h3>
                  <button
                    onClick={() => copyScript('initial', scripts.initial)}
                    className="flex items-center text-sm text-green-600 hover:text-green-700"
                  >
                    {copied === 'initial' ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{scripts.initial}</p>
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-gray-700">
                  <strong>When to use:</strong> First contact with the person
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Follow-Up #1 (No Response)</h3>
                  <button
                    onClick={() => copyScript('follow1', scripts.follow_up_1)}
                    className="flex items-center text-sm text-green-600 hover:text-green-700"
                  >
                    {copied === 'follow1' ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{scripts.follow_up_1}</p>
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-gray-700">
                  <strong>When to use:</strong> 5-7 days after initial message with no response
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Follow-Up #2 (Value-Add)</h3>
                  <button
                    onClick={() => copyScript('follow2', scripts.follow_up_2)}
                    className="flex items-center text-sm text-green-600 hover:text-green-700"
                  >
                    {copied === 'follow2' ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{scripts.follow_up_2}</p>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-gray-700">
                  <strong>When to use:</strong> 5-7 days after Follow-Up #1, shares valuable insight
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
